import { Schema } from 'effect';
import { SiteId } from './common.js';
/**
 * Request/response DTO schemas for the /admin group.
 *
 * Payloads that are whole Firestore documents or backend report objects are
 * left as `Schema.Any` — they are passed through untouched and pinning them
 * here would silently drop fields the admin UI reads.
 */
export const SiteParams = Schema.Struct({ siteId: SiteId });
export const OkResponse = Schema.Struct({ message: Schema.String });
// --- POST /admin/punch/edit/:siteId/:punchId ---------------------------------
export const PunchEditParams = Schema.Struct({
    siteId: SiteId,
    punchId: Schema.String
});
// --- POST /admin/calendar/override/:siteId/:itemKey --------------------------
export const CalendarOverrideParams = Schema.Struct({
    siteId: SiteId,
    itemKey: Schema.String
});
// --- POST /admin/booking/cancel/:siteId/:ticketKey ---------------------------
export const BookingCancelParams = Schema.Struct({
    siteId: SiteId,
    ticketKey: Schema.String
});
// --- POST /admin/set/calendar/public/:siteId/:yearMonth ----------------------
export const CalendarPublicParams = Schema.Struct({
    siteId: SiteId,
    yearMonth: Schema.String
});
// --- /admin/user/* -----------------------------------------------------------
export const UserLookupQuery = Schema.Struct({
    email: Schema.optional(Schema.String),
    log: Schema.optional(Schema.String)
});
export const UserLookupResponse = Schema.Struct({ user: Schema.Any });
export const UserUpdateParams = Schema.Struct({ uid: Schema.String });
export const UserUpdateBody = Schema.Struct({
    email: Schema.optional(Schema.String)
});
export const ShadowUserParams = Schema.Struct({
    siteId: SiteId,
    uid: Schema.String
});
// --- POST /admin/account/update/:siteId --------------------------------------
export const AccountUpdateParams = Schema.Struct({ siteId: SiteId });
// All fields optional — caller sends only what changes. Dates are ISO strings;
// empty string clears the field. tier change rotates the current plan into
// history (see backend `accountUpdate`).
export const AccountUpdateBody = Schema.Struct({
    trialEndsAt: Schema.optional(Schema.String),
    plan: Schema.optional(Schema.Struct({
        tier: Schema.optional(Schema.String),
        startedAt: Schema.optional(Schema.String),
        willEndAt: Schema.optional(Schema.String),
        ordersCredit: Schema.optional(Schema.Number)
    }))
});
export const ShadowUserResponse = Schema.Struct({
    success: Schema.Boolean,
    user: Schema.Any
});
// --- root reports ------------------------------------------------------------
export const CustomersResponse = Schema.Struct({ overviews: Schema.Any });
export const AuthStatsQuery = Schema.Struct({
    cutoffISO: Schema.optional(Schema.String),
    log: Schema.optional(Schema.String)
});
export const AuthStatsResponse = Schema.Struct({
    total: Schema.Number,
    verified: Schema.Number,
    unverified: Schema.Number,
    passwordUsers: Schema.Number,
    unverifiedPassword: Schema.Number,
    unverifiedInScope: Schema.Number,
    cutoffISO: Schema.String
});
// --- GET /admin/activity/* ---------------------------------------------------
// `since` is an ISO timestamp; omitted means "since the latest deploy".
export const ActivitySinceQuery = Schema.Struct({
    since: Schema.optional(Schema.String)
});
export const ActivitySinceResponse = Schema.Struct({
    since: Schema.String,
    deploys: Schema.Any,
    totals: Schema.Any,
    sites: Schema.Any,
    siteNames: Schema.Any,
    latest: Schema.Any,
    truncated: Schema.Boolean
});
export const ActivityDailyQuery = Schema.Struct({
    days: Schema.optional(Schema.String)
});
export const ActivityDailyResponse = Schema.Struct({
    days: Schema.Any,
    today: Schema.Any,
    siteNames: Schema.Any
});
export const BackfillResponse = Schema.Struct({
    success: Schema.Boolean,
    results: Schema.Any
});
// --- POST /admin/orders/process-{reserved,orphaned}/:siteId ------------------
export const ProcessOrdersQuery = Schema.Struct({
    hoursBack: Schema.optional(Schema.String),
    minAgeMinutes: Schema.optional(Schema.String),
    log: Schema.optional(Schema.String)
});
export const ProcessOrdersResponse = Schema.Struct({
    success: Schema.Boolean,
    siteId: Schema.String,
    hoursBack: Schema.Number,
    minAgeMinutes: Schema.Number,
    summary: Schema.Any
});
// --- /admin/app-version ------------------------------------------------------
// The per-site `minAppVersion` that makes older app bundles offer a reload
// (useVersionCheck). Old bundles read it from the site document only, so
// "all sites" is a write to every site document, not a platform setting.
export const AppVersionSite = Schema.Struct({
    siteId: Schema.String,
    name: Schema.String,
    minAppVersion: Schema.String
});
// A release that deployed the app, newest first. Only these are accepted as a
// minimum: any other value is a version no bundle carries, and every client
// would be asked to reload forever.
export const AppRelease = Schema.Struct({
    release: Schema.String,
    deployedAt: Schema.String
});
export const AppVersionResponse = Schema.Struct({
    sites: Schema.Array(AppVersionSite),
    releases: Schema.Array(AppRelease)
});
// `minAppVersion` '' clears the minimum.
export const AppVersionBody = Schema.Struct({
    minAppVersion: Schema.Trim.pipe(Schema.check(Schema.isMaxLength(40))),
    siteIds: Schema.Union([Schema.Literal('all'), Schema.Array(Schema.String)])
});
export const AppVersionUpdateResponse = Schema.Struct({
    updated: Schema.Array(Schema.String),
    missing: Schema.Array(Schema.String)
});
export const AppVersionProblem = {
    unknownRelease: 'problem.admin.appversion.unknownrelease',
    noSites: 'problem.admin.appversion.nosites'
};
// --- /admin/groups/* ---------------------------------------------------------
// Group membership. Definitions live in the site document and are saved with
// the rest of site data; only membership needs a server round trip, because
// firestore.rules denies client writes to shadowUsers. See docs/plans/groups.md.
export const GroupParams = Schema.Struct({
    siteId: SiteId,
    groupId: Schema.String
});
export const GroupMemberParams = Schema.Struct({
    siteId: SiteId,
    groupId: Schema.String,
    uid: Schema.String
});
export const GroupMembersResponse = Schema.Struct({
    members: Schema.Array(Schema.Struct({
        uid: Schema.String,
        name: Schema.String,
        email: Schema.String,
        membership: Schema.Struct({
            groupId: Schema.String,
            validFrom: Schema.String,
            validTo: Schema.String,
            addedBy: Schema.String,
            addedAt: Schema.String,
            note: Schema.String
        })
    }))
});
export const GroupAddMembersBody = Schema.Struct({
    // Free-form paste from the admin: one address per line or comma separated.
    emails: Schema.Array(Schema.String),
    validFrom: Schema.optional(Schema.String),
    validTo: Schema.optional(Schema.String),
    note: Schema.optional(Schema.String)
});
export const GroupAddMembersResponse = Schema.Struct({
    added: Schema.Array(Schema.String),
    // Addresses with no user here yet — parked as pending invites and claimed
    // when that address is next verified. See docs/plans/groups.md.
    invited: Schema.Array(Schema.String)
});
export const GroupPurgeResponse = Schema.Struct({
    removed: Schema.Number
});
// --- GET /admin/groups/:siteId/usersearch ------------------------------------
// The primary way an admin picks members: search this site's users by name or
// email fragment. Scoped to the site's shadow users on purpose — the same
// boundary as findShadowUserByEmail, so group admin cannot be used to probe
// whether an arbitrary address has a Velpr account elsewhere.
export const GroupUserSearchParams = Schema.Struct({
    siteId: SiteId
});
export const GroupUserSearchQuery = Schema.Struct({
    q: Schema.String
});
export const GroupUserSearchResponse = Schema.Struct({
    users: Schema.Array(Schema.Struct({
        uid: Schema.String,
        name: Schema.String,
        email: Schema.String
    }))
});
// --- /admin/groups/:siteId/:groupId/joincodes and /invites -------------------
// `uses` is absent on purpose: the server carries it forward from the stored
// code so an edit cannot reset a spent cap. See docs/plans/groups.md.
export const GroupJoinCodesBody = Schema.Struct({
    codes: Schema.Array(Schema.Struct({
        id: Schema.String,
        code: Schema.String,
        validFrom: Schema.String,
        validTo: Schema.String,
        maxUses: Schema.Number,
        membershipValidTo: Schema.String
    }))
});
export const GroupInviteParams = Schema.Struct({
    siteId: SiteId,
    groupId: Schema.String,
    email: Schema.String
});
//# sourceMappingURL=admin.js.map